import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import AuthorBadges from "@/components/community/AuthorBadges";
import { Users, MapPin, User, Search, MessageCircle } from "lucide-react";

const CANTONS = ["", "VD", "GE", "VS", "FR", "NE", "JU", "BE", "ZH", "LU", "SG", "TI", "BS", "BL", "AG", "GR"];
const ROLES = ["", "member", "artist", "organizer", "professional", "artisan", "team"];
const LANGS = [["", ""], ["fr", "FR"], ["de", "DE"], ["it", "IT"], ["en", "EN"]];

export default function CommunityMembers() {
  const { t } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ canton: "", lang: "", role: "", interest: "", q: "" });

  useEffect(() => { if (user) api.post("/community/region/sync").catch(() => {}); }, [user]);

  const { data } = useQuery({
    queryKey: ["comMembers", f],
    queryFn: async () => (await api.get("/community/members", { params: { canton: f.canton || undefined, lang: f.lang || undefined, role: f.role || undefined, interest: f.interest || undefined, q: f.q || undefined } })).data,
    enabled: !!user,
  });

  const handleLogin = () => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/community/members")}`; };
  const field = "bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200";

  if (loading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <Users className="w-12 h-12 text-amber-500 mx-auto mb-6" />
        <p className="text-stone-400 mb-8">{t("com_members_login")}</p>
        <button data-testid="members-login-btn" onClick={handleLogin} className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md">{t("login_google")}</button>
      </div>
    );
  }

  return (
    <>
      <Seo title={t("com_members_title")} description={t("com_members_intro")} canonicalPath="/community/members" noindex />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="mb-6 border-b border-amber-900/30 pb-5">
          <h1 className="font-serif text-4xl uppercase tracking-tight text-amber-100 flex items-center gap-3"><Users className="w-8 h-8 text-amber-500" /> {t("com_members_title")}</h1>
          <p className="text-stone-400 mt-2">{t("com_members_intro")}</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <div className="relative"><Search className="w-4 h-4 text-stone-500 absolute left-2.5 top-2.5" /><input data-testid="members-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder={t("com_search_ph")} className={`${field} pl-8`} /></div>
          <select data-testid="members-canton" value={f.canton} onChange={(e) => setF({ ...f, canton: e.target.value })} className={field}><option value="">{t("com_filter_region")}</option>{CANTONS.filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}</select>
          <select data-testid="members-lang" value={f.lang} onChange={(e) => setF({ ...f, lang: e.target.value })} className={field}><option value="">{t("com_filter_language")}</option>{LANGS.filter(([v]) => v).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <select data-testid="members-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={field}><option value="">{t("com_role_all")}</option>{ROLES.filter(Boolean).map((rq) => <option key={rq} value={rq}>{t(`com_role_${rq}`)}</option>)}</select>
          <input data-testid="members-interest" value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} placeholder={t("com_filter_interest")} className={field} />
        </div>

        {!data?.members?.length ? (
          <div className="text-center py-16 text-stone-500 border border-dashed border-[#272A34] rounded-lg" data-testid="members-empty">{t("com_members_empty")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="members-grid">
            {data.members.map((m) => (
              <Link key={m.user_id} to={`/u/${m.user_id}`} data-testid={`member-${m.user_id}`} className="bg-[#121418] border border-[#272A34] hover:border-amber-500/40 rounded-lg p-4 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  {m.picture ? <img src={m.picture} alt="" className="w-12 h-12 rounded-full object-cover" /> : <span className="w-12 h-12 rounded-full bg-amber-900/40 flex items-center justify-center"><User className="w-6 h-6 text-amber-400" /></span>}
                  <div className="min-w-0"><h3 className="font-serif text-stone-100 truncate">{m.name}</h3>{m.canton && <span className="text-xs text-stone-500 font-mono flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.canton}</span>}</div>
                </div>
                <AuthorBadges badges={m.badges} className="mb-2" />
                {m.languages?.length > 0 && <div className="text-[10px] font-mono uppercase text-stone-500 mb-1">{m.languages.join(" · ")}</div>}
                {m.interests?.length > 0 && <div className="flex flex-wrap gap-1">{m.interests.slice(0, 4).map((i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#181A20] border border-[#272A34] text-stone-400">{i}</span>)}</div>}
                {user && m.user_id !== user.user_id && (
                  <button
                    data-testid={`message-member-${m.user_id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/messages/${m.user_id}`); }}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-md bg-amber-600/15 border border-amber-700/40 text-amber-300 hover:bg-amber-600/25 transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> {t("com_message_btn") || "Message"}
                  </button>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
